import type { Schritt } from "@fbs/funnel-schema";

import {
  alsObjekt,
  alsText,
  fehlerText,
  kontaktBeschriftung,
  KONTAKT_FELDER,
  type SchrittProps,
} from "./gemeinsam";

type Kontakt = Extract<Schritt, { type: "contact" }>;
type Einwilligung = Extract<Schritt, { type: "consent" }>;
type Information = Extract<Schritt, { type: "information" }>;

export function KontaktAnsicht({
  schritt,
  wert,
  fehler,
  teil,
  onWert,
  onWeiter,
}: SchrittProps<Kontakt>) {
  const daten = alsObjekt(wert);

  /**
   * Die Felder dieses Bildschirms.
   *
   * `teil` zählt ab 0, die Bildschirmnummer im Schema ab 1. Ist der Schritt
   * nicht geteilt, stehen alle Felder zusammen auf einer Seite.
   */
  const sichtbare =
    teil === null ? schritt.fields : schritt.fields.filter((f) => f.screen === teil + 1);

  /** Der letzte Bildschirm dieses Schritts, gezählt ab 0 wie `teil`. */
  const letzterTeil = Math.max(...schritt.fields.map((f) => f.screen)) - 1;
  const aufLetztem = teil === null || teil === letzterTeil;

  /**
   * Bezieht sich auf den Stand zum Zeitpunkt der Änderung, nicht auf den
   * beim Rendern gelesenen.
   *
   * Ohne das gehen Eingaben verloren, sobald mehrere Felder im selben
   * Augenblick gefüllt werden - und genau das macht die Autovervollständigung
   * des Browsers, für die die autocomplete-Angaben ja da sind.
   */
  const setzen = (name: string, eingabe: string) => {
    onWert((vorher: unknown) => ({ ...alsObjekt(vorher), [name]: eingabe }));
  };

  return (
    <div class="f-feld f-kontaktfelder">
      {sichtbare.map((feld) => {
        const beschreibung = KONTAKT_FELDER[feld.name];
        const meldung = fehlerText(fehler, feld.name);
        const beschriftung = kontaktBeschriftung(feld);

        return (
          <label
            class={`f-label${feld.width === "half" ? " f-label--halb" : ""}`}
            key={feld.name}
          >
            <span class="f-feldzeile">
              <input
                type={beschreibung.typ}
                inputMode={beschreibung.inputmode}
                autoComplete={beschreibung.autocomplete}
                class={`f-eingabe${meldung ? " f-eingabe--fehler" : ""}${
                  feld.info ? " f-eingabe--mit-info" : ""
                }`}
                value={alsText(daten[feld.name])}
                // Der Platzhalter ist die Beschriftung. Eine zusätzliche
                // Beschriftung darüber verdoppelt bei diesen Feldern nur den
                // Text, denn "Vorname" braucht keine Erklärung.
                placeholder={`${beschriftung}${feld.required ? "" : " (optional)"}`}
                aria-label={beschriftung}
                aria-invalid={meldung !== null}
                onInput={(e) => {
                  setzen(feld.name, e.currentTarget.value);
                }}
                onKeyDown={(e) => {
                  // Enter geht weiter, aber nur wenn dieser Bildschirm ein
                  // einzelnes Feld hat. Sonst überspränge man die anderen.
                  if (e.key === "Enter" && sichtbare.length === 1) onWeiter();
                }}
              />

              {/*
                Das Infozeichen steht im Feld, nicht als Zeile darunter.

                Die Erklärung zur Telefonnummer ist der Satz, den genau die
                Leute suchen, die zögern - und für alle anderen eine Zeile
                Kleingedrucktes mehr auf dem letzten Bildschirm. Hinter einem
                Zeichen ist sie da, wenn man sie braucht, und sonst nicht im
                Weg.

                Der Knopf fängt den Klick ab: Ohne das reichte das Label ihn
                an das Eingabefeld weiter, und auf dem Handy ginge die
                Tastatur auf, während jemand nur lesen wollte.
              */}
              {feld.info ? (
                <span class="f-erklaerung">
                  <button
                    type="button"
                    class="f-erklaerung__zeichen"
                    aria-label={"Warum wir danach fragen: " + beschriftung}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <span aria-hidden="true">i</span>
                  </button>
                </span>
              ) : null}
            </span>

            {/*
              Der Satz steht unter dem Feld und schiebt auf, statt zu
              ueberlagern.

              Als schwebende Blase deckte er je nach Geraet die Frage darueber
              oder die Einwilligung darunter zu - am Handy, wo der meiste
              Verkehr herkommt, war kein Platz, an dem er nichts verdeckt
              haette. Aufschiebend braucht er zwar Hoehe, aber er nimmt sie
              sich nur, solange jemand ihn liest.
            */}
            {feld.info ? (
              <span class="f-erklaerung__text" role="tooltip">
                <span class="f-erklaerung__innen">{feld.info}</span>
              </span>
            ) : null}
            {feld.hint ? <span class="f-hinweis">{feld.hint}</span> : null}
            {meldung ? <span class="f-fehler">{meldung}</span> : null}
          </label>
        );
      })}

      {/*
        Der Rechtstext steht dort, wo die Angaben gemacht werden, und nicht
        erst zwei Bildschirme spaeter. Er ersetzt die Einwilligung nicht - ein
        Haken, den jemand setzt, ist etwas anderes als ein Satz, den er
        ueberlesen hat.

        Nur auf dem ersten Bildschirm des Schritts. Ein geteilter
        Kontaktschritt hat mehrere, und derselbe Absatz zweimal hintereinander
        liest sich wie ein Fehler - beim zweiten Mal ueberliest man ihn ohnehin.
      */}
      {/*
        Die Haken stehen auf dem letzten Bildschirm des Schritts, direkt über
        dem Absende-Knopf: Zustimmung und die Handlung, für die sie gilt,
        gehören zusammen. Ein eigener Bildschirm nur für einen Haken wäre ein
        Klick ohne Gegenwert.
      */}
      {schritt.consent_items.length > 0 && aufLetztem ? (
        <div class="f-kontakt__haken">
          {schritt.consent_items.map((eintrag) => {
            const hakenfehler = fehlerText(fehler, eintrag.key);

            return (
              <div key={eintrag.key}>
                <label class={"f-haken" + (hakenfehler ? " f-haken--fehler" : "")}>
                  <input
                    type="checkbox"
                    checked={daten[eintrag.key] === true}
                    aria-invalid={hakenfehler !== null}
                    onChange={(e) => {
                      const gesetzt = e.currentTarget.checked;
                      onWert((vorher: unknown) => ({
                        ...alsObjekt(vorher),
                        [eintrag.key]: gesetzt,
                      }));
                    }}
                  />
                  <span class="f-haken__kasten" aria-hidden="true">
                    <svg viewBox="0 0 20 20" width="13" height="13">
                      <path
                        d="M4 10.5 8 14.5 16 6"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.75"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <span class="f-haken__text">
                    {eintrag.label}
                    {eintrag.link ? (
                      <>
                        {" "}
                        <a
                          href={eintrag.link.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          // Ohne das schaltet der Tipp auf den Link den Haken
                          // gleich mit um.
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {eintrag.link.text}
                        </a>
                      </>
                    ) : null}
                  </span>
                </label>
                {hakenfehler ? <p class="f-fehler">{hakenfehler}</p> : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {schritt.legal_note && (teil === null || teil === 0) ? (
        <p class="f-rechtstext">{mitLinks(schritt.legal_note)}</p>
      ) : null}
    </div>
  );
}

/**
 * Macht aus [Text](/pfad) einen Link, aus allem anderen Text.
 *
 * Bewusst kein HTML im Feld: Der Wert kommt aus dem Builder und damit von
 * einem Menschen, aber ein Feld, das Auszeichnung annimmt, nimmt sie von
 * jedem an. Erlaubt sind eigene Pfade und https-Adressen.
 */
export function mitLinks(text: string): (string | preact.JSX.Element)[] {
  const teile: (string | preact.JSX.Element)[] = [];
  const muster = /\[([^\]]{1,80})\]\((\/[^\s)]{0,120}|https:\/\/[^\s)]{1,200})\)/g;
  let zuletzt = 0;
  let treffer: RegExpExecArray | null;

  while ((treffer = muster.exec(text)) !== null) {
    if (treffer.index > zuletzt) teile.push(text.slice(zuletzt, treffer.index));
    teile.push(
      <a href={treffer[2]} target="_blank" rel="noopener noreferrer" key={treffer.index}>
        {treffer[1]}
      </a>,
    );
    zuletzt = treffer.index + treffer[0].length;
  }
  if (zuletzt < text.length) teile.push(text.slice(zuletzt));
  return teile;
}

export function EinwilligungAnsicht({
  schritt,
  wert,
  fehler,
  onWert,
}: SchrittProps<Einwilligung>) {
  const daten = alsObjekt(wert);

  return (
    <div class="f-feld f-feld--gruppe">
      {schritt.items.map((eintrag) => {
        const meldung = fehlerText(fehler, eintrag.key);
        const an = daten[eintrag.key] === true;

        return (
          <div key={eintrag.key}>
            <label class={`f-haken${meldung ? " f-haken--fehler" : ""}`}>
              <input
                type="checkbox"
                checked={an}
                aria-invalid={meldung !== null}
                onChange={(e) => {
                  const gesetzt = e.currentTarget.checked;
                  onWert((vorher: unknown) => ({
                    ...alsObjekt(vorher),
                    [eintrag.key]: gesetzt,
                  }));
                }}
              />
              <span class="f-haken__kasten" aria-hidden="true">
                <svg viewBox="0 0 20 20" width="13" height="13">
                  <path
                    d="M4 10.5 8 14.5 16 6"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="f-haken__text">
                {eintrag.label}
                {eintrag.required ? null : <span class="f-hinweis"> (freiwillig)</span>}
                {eintrag.link ? (
                  <>
                    {" "}
                    <a
                      href={eintrag.link.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      // Ohne das öffnet der Tipp auf den Link die Einwilligung
                      // gleich mit oder wieder ab.
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {eintrag.link.text}
                    </a>
                  </>
                ) : null}
              </span>
            </label>
            {meldung ? <p class="f-fehler">{meldung}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

export function InformationAnsicht({ schritt }: SchrittProps<Information>) {
  /*
   * Mit Ladekreis ist die Seite eine Statusmeldung, keine Lesetafel.
   * role="status" sagt das dem Screenreader, sonst bleibt der Wechsel
   * unangekuendigt und jemand steht vor einem Bildschirm, der sich von selbst
   * weiterschaltet.
   */
  return (
    <div
      class="f-info"
      role={schritt.loader ? "status" : undefined}
      aria-live={schritt.loader ? "polite" : undefined}
    >
      {schritt.media?.type === "image" && !schritt.loader ? (
        <img class="f-info__bild" src={schritt.media.src} alt="" loading="lazy" />
      ) : null}
      {/*
        Keine eigene Ueberschrift hier.

        Die Huelle zeichnet sie bereits als .f-frage - fuer einen
        Informationsschritt nimmt sie dessen headline. Wer sie hier noch einmal
        setzt, bekommt sie zweimal untereinander, und die zweite ist die
        kleinere: Es sieht aus wie ein Fehler, weil es einer ist.

        Die Ueberschrift dort oben ist ausserdem die, auf die der Fokus beim
        Bildschirmwechsel springt. Eine zweite waere fuer den Screenreader ein
        Vorlesepunkt, den niemand ansteuert.
      */}
      {schritt.body ? <p class="f-info__text">{schritt.body}</p> : null}
      {schritt.loader ? <div class="f-info__ring" aria-hidden="true" /> : null}
    </div>
  );
}
